(() => {
  if (customElements.get('email-popups')) return;
  const prefix = 'smud:email-popups:';
  const read = (key) => { try { return localStorage.getItem(prefix + key); } catch { return null; } };
  const write = (key, value) => { try { localStorage.setItem(prefix + key, value); return true; } catch { return false; } };
  const lines = (value) => String(value || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  class EmailPopups extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.popups = [...this.querySelectorAll('dialog')];
      this.editor = Boolean(window.Shopify?.designMode);
      this.preview = window.Shopify?.theme?.role === 'unpublished' && new URLSearchParams(location.search).get('email_popup_preview') === '1';
      if (!this.editor && !this.preview) this.recordVisit();
      this.popups.forEach(popup => {
        popup.rules = JSON.parse(popup.querySelector('[data-rules]').textContent);
        popup.querySelector('[data-close]').addEventListener('click', () => popup.close(), { signal });
        popup.addEventListener('click', event => {
          if (event.target !== popup) return;
          const r = popup.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) popup.close();
        }, { signal });
        popup.addEventListener('close', () => this.onClose(popup), { signal });
        popup.addEventListener('keydown', event => {
          if (event.key !== 'Tab') return;
          const stops = [...popup.querySelectorAll('button, a[href], input, select, textarea, iframe, [tabindex]')]
            .filter(el => el.tabIndex >= 0 && !el.matches(':disabled') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
          const first = stops[0], last = stops.at(-1);
          if (!first) { event.preventDefault(); popup.focus(); return; }
          if (event.shiftKey && (document.activeElement === first || document.activeElement === popup)) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first.focus();
          }
        }, { signal });
        popup.querySelector('[data-reveal]').addEventListener('click', () => this.reveal(popup), { signal });
        popup.querySelector('[data-claim]').addEventListener('click', () => this.showForm(popup), { signal });
        popup.querySelector('[data-retry]').addEventListener('click', () => this.watchForm(popup), { signal });
      });
      window.addEventListener('klaviyoForms', event => this.onKlaviyo(event), { signal });
      window.addEventListener('storage', () => {
        if (this.active && !this.editor && !this.preview && read('subscribed') && this.active.rules.hideSubscribers) this.active.close();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.ready) this.scheduleOpen();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        if (!this.editor) return;
        const popup = this.popups.find(p => p.dataset.blockId === event.detail.blockId);
        if (popup) this.open(popup);
      }, { signal });
      document.addEventListener('shopify:section:select', event => {
        if (this.editor && event.target.contains(this) && this.popups[0]) this.open(this.popups[0]);
      }, { signal });
      document.addEventListener('shopify:section:deselect', event => {
        if (this.editor && event.target.contains(this)) this.active?.close();
      }, { signal });
      if (!this.editor && this.dataset.enabled === 'true') {
        this.timer = setTimeout(() => { this.ready = true; this.scheduleOpen(); }, Number(this.dataset.delay) * 1000);
      }
    }

    disconnectedCallback() {
      this.active?.close();
      this.restoreScroll();
      this.abort?.abort();
      clearTimeout(this.timer);
      clearTimeout(this.formTimer);
      clearTimeout(this.retryTimer);
      this.formObserver?.disconnect();
      this.scratchObserver?.disconnect();
    }

    recordVisit() {
      let visit;
      try { visit = JSON.parse(read('visit')); } catch { /* Ignore invalid storage. */ }
      const now = Date.now();
      this.newVisit = !visit || (visit.first === true && now - visit.last < 30 * 60 * 1000);
      const source = new URLSearchParams(location.search);
      const campaign = ['utm_source', 'utm_medium'].some(key => /^(klaviyo|email|sms|text)$/i.test(source.get(key) || ''));
      if (campaign || /\/(account|thank_you)(\/|$)/.test(location.pathname)) write('known', 'true');
      // Store on all pages, including those that do not qualify for a popup.
      if (!write('visit', JSON.stringify({ first: this.newVisit, last: now }))) this.newVisit = false;
    }

    matches(popup) {
      const r = popup.rules;
      const path = location.pathname; // Query strings must never satisfy path conditions.
      if (!r.enabled || !/^[a-zA-Z0-9]+$/.test(popup.dataset.formId)) return false;
      if (!this.preview && r.newVisitors && (!this.newVisit || this.dataset.customer === 'true' || read('known'))) return false;
      if (!this.preview && r.hideSubscribers && (this.dataset.subscribed === 'true' || read('subscribed') || localStorageSafe('theme:popup-filled') === 'true')) return false;
      if (!this.preview && read('shown')) return false;
      if (r.products?.length && !r.products.includes(this.dataset.product)) return false;
      const included = lines(r.includePaths);
      if (included.length && !included.some(fragment => path.includes(fragment))) return false;
      if (lines(r.excludePaths).some(fragment => path.includes(fragment))) return false;
      const countries = String(r.countries || '').toUpperCase().split(/[\s,;]+/).filter(Boolean);
      if (countries.length) {
        if (!this.dataset.country) return false;
        const inCountry = countries.includes(this.dataset.country.toUpperCase());
        if (r.countryMode === 'exclude' ? inCountry : !inCountry) return false;
      }
      const mobile = matchMedia('(max-width: 749px)').matches;
      return !(r.devices === 'mobile' && !mobile || r.devices === 'desktop' && mobile);
    }

    hasBlockingOverlay() {
      return [...document.querySelectorAll('dialog[open], .drawer[open], [aria-modal="true"], .mh1--mega-open, .mh1--mobile-mega-open')].some(element => {
        const style = getComputedStyle(element);
        return element.getClientRects().length && style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
      });
    }

    async scheduleOpen() {
      if (this.checking || this.active || !this.isConnected || document.hidden || this.editor) return;
      const popup = this.popups.find(p => this.matches(p));
      if (!popup) return;
      // Do not interrupt the cart, search, or another modal.
      if (this.hasBlockingOverlay()) {
        clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => this.scheduleOpen(), 1000);
        return;
      }
      this.checking = true;
      let known = false;
      if (!this.preview && (popup.rules.newVisitors || popup.rules.hideSubscribers) && typeof window.klaviyo?.isIdentified === 'function') {
        // Klaviyo exposes identity, not a public subscription lookup. Suppress all
        // identified profiles conservatively; never infer consent from a cookie.
        let identityTimer;
        try {
          known = await Promise.race([
            window.klaviyo.isIdentified(),
            new Promise(resolve => { identityTimer = setTimeout(() => resolve(null), 1500); })
          ]);
        } catch { known = null; }
        clearTimeout(identityTimer);
      }
      this.checking = false;
      if (known === true) { write('known', 'true'); return; }
      if (known === null) {
        // An unavailable identity service is not evidence of a known visitor.
        // Retry briefly without poisoning eligibility on future page loads.
        this.identityRetries = (this.identityRetries || 0) + 1;
        if (this.identityRetries <= 3 && this.isConnected) {
          clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => this.scheduleOpen(), 2000);
        }
        return;
      }
      if (this.isConnected && !document.hidden && this.matches(popup)) {
        if (this.hasBlockingOverlay()) {
          clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => this.scheduleOpen(), 1000);
        } else this.open(popup);
      }
    }

    open(popup) {
      if (this.active === popup && popup.open) return;
      if (this.active) { const previous = this.active; previous.close(); this.onClose(previous); }
      this.active = popup;
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      popup.showModal();
      popup.focus({ preventScroll: true });
      if (!this.editor && !this.preview) write('shown', 'true');
      if (!popup.scratchReady) this.setupScratch(popup);
    }

    restoreScroll() {
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }

    onClose(popup) {
      if (this.active !== popup) return;
      this.restoreScroll();
      this.active = null;
      clearTimeout(this.formTimer);
      this.formObserver?.disconnect();
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    }

    setupScratch(popup) {
      popup.scratchReady = true;
      const canvas = popup.querySelector('canvas');
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) { this.reveal(popup); return; }
      const cover = new Image();
      cover.crossOrigin = 'anonymous';
      const paint = () => {
        if (popup.revealed) return;
        const rect = canvas.getBoundingClientRect();
        if (!rect.width) return;
        // Fixed working resolution makes threshold sampling cheap and independent
        // of retina screens. Browser scales pointer coordinates into this space.
        canvas.width = 600;
        canvas.height = 440;
        context.globalCompositeOperation = 'source-over';
        context.fillStyle = '#d3af4f';
        context.fillRect(0, 0, 600, 440);
        if (cover.complete && cover.naturalWidth) context.drawImage(cover, 0, 0, 600, 440);
        else {
          context.fillStyle = '#fff3ba';
          context.font = 'bold 46px Arial';
          context.textAlign = 'center';
          context.fillText('RASPÁ ACÁ', 300, 240);
        }
      };
      cover.onload = () => { if (!popup.scratchStarted) paint(); };
      cover.src = canvas.dataset.cover;
      paint();
      let previous = null;
      let distance = 0;
      const point = event => {
        const rect = canvas.getBoundingClientRect();
        return { x: (event.clientX - rect.left) * 600 / rect.width, y: (event.clientY - rect.top) * 440 / rect.height };
      };
      const scratch = event => {
        if (!previous || popup.revealed) return;
        const next = point(event);
        popup.scratchStarted = true;
        context.globalCompositeOperation = 'destination-out';
        context.lineWidth = 65;
        context.lineCap = 'round';
        context.beginPath();
        context.moveTo(previous.x, previous.y);
        context.lineTo(next.x, next.y);
        context.stroke();
        distance += Math.hypot(next.x - previous.x, next.y - previous.y);
        previous = next;
      };
      const finish = () => {
        if (!previous || popup.revealed) return;
        previous = null;
        const threshold = Number(popup.querySelector('[data-scratch]').dataset.threshold) / 100;
        try {
          const pixels = context.getImageData(0, 0, 600, 440).data;
          let clear = 0, sampled = 0;
          for (let i = 3; i < pixels.length; i += 64) { sampled++; if (pixels[i] < 128) clear++; }
          if (clear / sampled >= threshold) this.reveal(popup);
        } catch {
          // A merchant image may not permit pixel reads. Keep reveal usable.
          if (distance > 600 * 440 * threshold / 45) this.reveal(popup);
        }
      };
      const signal = this.abort.signal;
      canvas.addEventListener('pointerdown', event => {
        if (event.button !== 0 || popup.revealed) return;
        previous = point(event);
        canvas.setPointerCapture(event.pointerId);
        event.preventDefault();
      }, { signal });
      canvas.addEventListener('pointermove', scratch, { signal });
      canvas.addEventListener('pointerup', finish, { signal });
      canvas.addEventListener('pointercancel', finish, { signal });
      canvas.addEventListener('lostpointercapture', finish, { signal });
    }

    reveal(popup) {
      if (popup.revealed) return;
      popup.revealed = true;
      popup.querySelector('[data-scratch]').classList.add('is-revealed');
      popup.querySelector('[data-reveal]').hidden = true;
      popup.querySelector('[data-claim]').hidden = false;
      popup.querySelector('.ep__prize').removeAttribute('aria-hidden');
      popup.querySelector('[data-announcement]').textContent = popup.querySelector('.ep__prize').textContent.trim();
      popup.querySelector('[data-claim]').focus({ preventScroll: true });
    }

    showForm(popup) {
      popup.querySelector('[data-game]').hidden = true;
      const form = popup.querySelector('[data-form]');
      form.removeAttribute('data-staged');
      form.removeAttribute('inert');
      form.removeAttribute('aria-hidden');
      const heading = popup.querySelector('[data-form-heading]');
      heading.id = popup.id + '-form-title';
      popup.setAttribute('aria-labelledby', heading.id);
      heading.focus({ preventScroll: true });
      this.watchForm(popup);
    }

    watchForm(popup) {
      this.formObserver?.disconnect();
      clearTimeout(this.formTimer);
      const embed = popup.querySelector('[data-embed]');
      const status = popup.querySelector('[data-form-status]');
      popup.loadingLabel ||= status.textContent;
      status.hidden = false;
      status.textContent = popup.loadingLabel;
      popup.querySelector('[data-retry]').hidden = true;
      const ready = () => {
        if (!embed?.querySelector('input, button, form, iframe')) return false;
        status.hidden = true;
        popup.querySelector('[data-retry]').hidden = true;
        clearTimeout(this.formTimer);
        this.formObserver?.disconnect();
        return true;
      };
      if (ready()) return;
      if (embed) {
        this.formObserver = new MutationObserver(ready);
        this.formObserver.observe(embed, { childList: true, subtree: true });
      }
      this.formTimer = setTimeout(() => {
        if (ready()) return;
        status.textContent = this.querySelector('[data-form-error]').textContent;
        popup.querySelector('[data-retry]').hidden = false;
      }, 10000);
    }

    onKlaviyo(event) {
      if (this.editor) return;
      const { type, formId } = event.detail || {};
      if (type !== 'submit') return;
      // Listen to all Klaviyo signups to suppress acquisition popups later.
      if (!this.preview) write('subscribed', 'true');
      const popup = this.active;
      if (!popup) return;
      if (formId !== popup.dataset.formId) {
        if (popup.rules.hideSubscribers) popup.close();
        return;
      }
      // submit is the FIRST conversion, not the end of a multi-step form.
      // Leave Klaviyo mounted so SMS consent, Skip and success keep working.
      popup.querySelector('[data-form-heading]').hidden = true;
      popup.setAttribute('aria-label', 'Completá tu suscripción');
      popup.removeAttribute('aria-labelledby');
    }
  }
  function localStorageSafe(key) { try { return localStorage.getItem(key); } catch { return null; } }
  customElements.define('email-popups', EmailPopups);
})();
